import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FiHome, FiEdit, FiInbox, FiBarChart2, FiSettings,
  FiCpu, FiFileText, FiLogOut, FiMenu, FiX
} from 'react-icons/fi';
import ContentEditor from '../components/admin/ContentEditor';
import InquiriesManager from '../components/admin/InquiriesManager';
import AnalyticsDashboard from '../components/admin/AnalyticsDashboard';
import SettingsPanel from '../components/admin/SettingsPanel';
import ContentStudio from '../components/admin/ContentStudio';
import AuditLog from '../components/admin/AuditLog';
import { getMe } from '../services/api';

function AdminDashboard({ onLogout }) {
  const [userRole, setUserRole] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    loadUserInfo();
  }, []);

  const loadUserInfo = async () => {
    try {
      const response = await getMe();
      setUserRole(response.data.role);
    } catch (error) {
      console.error('Failed to load user info:', error);
      handleLogout();
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_role');
    onLogout();
    navigate('/admin/login');
  };

  const navItems = [
    { path: '', label: 'Overview', icon: FiHome, roles: ['admin', 'editor'] },
    { path: 'content', label: 'Content Editor', icon: FiEdit, roles: ['admin', 'editor'] },
    { path: 'inquiries', label: 'Inquiries', icon: FiInbox, roles: ['admin'] },
    { path: 'analytics', label: 'Analytics', icon: FiBarChart2, roles: ['admin'] },
    { path: 'studio', label: 'Content Studio', icon: FiCpu, roles: ['admin'] },
    { path: 'settings', label: 'Settings', icon: FiSettings, roles: ['admin'] },
    { path: 'audit', label: 'Audit Log', icon: FiFileText, roles: ['admin'] },
  ];

  const filteredNavItems = navItems.filter(item => item.roles.includes(userRole));

  return (
    <div className="flex h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* Sidebar */}
      <motion.aside
        initial={{ x: -300 }}
        animate={{ x: isSidebarOpen ? 0 : -300 }}
        className="fixed lg:static inset-y-0 left-0 z-40 w-64 bg-dark-900 text-white flex flex-col"
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-700">
          <h1 
            className="text-3xl font-bold tracking-wider"
            style={{
              fontFamily: "'Racing Sans One', cursive",
              color: '#FFFFFF'
            }}
          >
            HADEN
          </h1>
          <p className="text-sm text-gray-400 mt-1 capitalize">{userRole} Panel</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {filteredNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === `/admin/dashboard/${item.path}` || 
                            (item.path === '' && location.pathname === '/admin/dashboard');
            
            return (
              <Link
                key={item.path}
                to={`/admin/dashboard/${item.path}`}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-primary-600 text-white'
                    : 'text-gray-300 hover:bg-gray-800'
                }`}
                data-testid={`nav-${item.path || 'overview'}`}
              >
                <Icon className="text-xl" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="p-4 border-t border-gray-700">
          <button
            onClick={handleLogout}
            className="flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-300 hover:bg-gray-800 w-full transition-colors"
            data-testid="logout-btn"
          >
            <FiLogOut className="text-xl" />
            <span>Logout</span>
          </button>
        </div>
      </motion.aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 p-4 lg:p-6 flex items-center justify-between">
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="lg:hidden text-2xl text-gray-600"
          >
            {isSidebarOpen ? <FiX /> : <FiMenu />}
          </button>
          
          <div className="flex-1"></div>
          
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            View Website →
          </a>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-8">
          <Routes>
            <Route path="/" element={<Overview userRole={userRole} />} />
            <Route path="/content" element={<ContentEditor />} />
            <Route path="/inquiries" element={<InquiriesManager />} />
            <Route path="/analytics" element={<AnalyticsDashboard />} />
            <Route path="/studio" element={<ContentStudio />} />
            <Route path="/settings" element={<SettingsPanel />} />
            <Route path="/audit" element={<AuditLog />} />
          </Routes>
        </main>
      </div>

      {/* Overlay for mobile */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  );
}

// Overview Component
function Overview({ userRole }) {
  return (
    <div data-testid="overview-panel">
      <h1 className="text-3xl font-bold text-dark-900 mb-6">Dashboard Overview</h1>
      
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm">
          <h3 className="text-lg font-semibold text-dark-900 mb-2">Welcome</h3>
          <p className="text-gray-600">
            Manage your HADEN Systems website content, inquiries, and settings.
          </p>
        </div>

        {userRole === 'admin' && (
          <>
            <div className="bg-primary-50 p-6 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold text-primary-900 mb-2">Quick Actions</h3>
              <ul className="space-y-2 text-sm text-primary-700">
                <li>• Review new inquiries</li>
                <li>• Update content with AI Studio</li>
                <li>• Check analytics</li>
              </ul>
            </div>

            <div className="bg-dark-50 p-6 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold text-dark-900 mb-2">Admin Access</h3>
              <p className="text-gray-600 text-sm">
                You have full access to all features including settings, analytics, and audit logs.
              </p>
            </div>
          </>
        )}

        {userRole === 'editor' && (
          <div className="bg-primary-50 p-6 rounded-lg shadow-sm">
            <h3 className="text-lg font-semibold text-primary-900 mb-2">Editor Access</h3>
            <p className="text-primary-700 text-sm">
              You can edit website content and view basic information.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
